import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: 'Margaret Thompson',
    relation: 'Daughter of Care Recipient',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face',
    rating: 5,
    text: "Heritage Home Health LLC has been an absolute blessing for our family. The caregiver assigned to my mother treats her with such genuine warmth and respect. For the first time in years, I feel at peace knowing Mom is in caring hands.",
  },
  {
    id: 2,
    name: 'Robert Chen',
    relation: 'Son of Care Recipient',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
    rating: 5,
    text: "After trying several agencies, Heritage Home Health LLC stood out immediately. Their attention to detail, consistent communication, and the quality of their caregivers is unmatched. Dad actually looks forward to his caregiver's visits now.",
  },
  {
    id: 3,
    name: 'Patricia Martinez',
    relation: 'Care Recipient',
    image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face',
    rating: 5,
    text: "I was hesitant about accepting help at first, but the team at Heritage Home Health LLC made me feel comfortable from day one. They've helped me maintain my independence while ensuring I'm safe. I'm grateful every day.",
  },
];

export const TestimonialsSection = () => {
  return (
    <section className="relative py-24 md:py-32 bg-white overflow-hidden" data-testid="testimonials-section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-warm-beige/30 via-transparent to-warm-beige/30" />
      <div className="absolute top-20 right-20 w-[400px] h-[400px] bg-soft-gold/5 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
            Testimonials
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-navy mb-6">
            What Families Say About{' '}
            <span className="text-gradient-gold">Heritage Home Health LLC</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Real stories from families who have experienced the compassionate care 
            and exceptional service of Heritage Home Health LLC.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              className="group relative"
              data-testid={`testimonial-card-${testimonial.id}`}
            >
              <div className="relative h-full glass-strong shadow-soft-3d rounded-3xl p-8 hover:shadow-soft-3d-lg transition-all duration-500 hover:-translate-y-2">
                {/* Quote Icon */}
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-soft-gold/20 flex items-center justify-center">
                  <Quote className="w-6 h-6 text-soft-gold" />
                </div>

                {/* Rating */}
                <div className="flex items-center space-x-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-soft-gold text-soft-gold" />
                  ))}
                </div>

                {/* Quote Text */}
                <p className="text-gray-600 leading-relaxed mb-8 text-base">
                  "{testimonial.text}"
                </p>

                {/* Author */}
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-soft-gold/20"
                    />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-muted-green flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-navy">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.relation}</p>
                  </div>
                </div>

                {/* Decorative Element */}
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-tl from-soft-gold/10 to-transparent rounded-br-3xl rounded-tl-[100px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust Badge */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center space-x-4 glass rounded-full px-8 py-4">
            <div className="flex items-center space-x-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="w-5 h-5 fill-soft-gold text-soft-gold" />
              ))}
            </div>
            <div className="h-8 w-px bg-soft-gray" />
            <p className="text-gray-600">
              <span className="font-bold text-navy">4.9/5</span> average rating from{' '}
              <span className="font-bold text-navy">500+</span> reviews
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
